<template>
	<div
		class="supplier-performance-world-map"
		@mouseleave="scheduleClearHover"
	>
		<svg
			class="world-map-svg"
			:viewBox="world.viewBox"
			role="img"
			aria-label="Supplier performance world map"
		>
			<defs>
				<clipPath
					v-for="clip in splitClipPaths"
					:key="clip.key"
					:id="clip.id"
					clipPathUnits="objectBoundingBox"
				>
					<rect :x="clip.x" y="0" :width="clip.width" height="1" />
				</clipPath>
			</defs>

			<!-- Base layer: countries without scores -->
			<path
				v-for="location in inactiveLocations"
				:key="`base-${location.id}`"
				:d="location.path"
				class="world-map-region"
				:style="{ fill: MAP_TIER_COLORS.default }"
			/>

			<!-- Active countries on top; tiny countries get a score-colored dot -->
			<template v-for="location in activeLocations">
				<template v-if="isSplitCountry(location.id)">
					<g
						:key="`active-split-${location.id}`"
					>
						<path
							v-for="(slice, index) in getSplitSlices(location.id)"
							:key="`${location.id}-slice-${index}`"
							:d="location.path"
							class="world-map-region world-map-region--split"
							:class="regionClasses(location.id)"
							:style="{ fill: slice.fill }"
							:clip-path="`url(#${clipPathId(location.id, index)})`"
							@mouseenter="setCountryHover(location.id, $event)"
						/>
						<path
							:d="location.path"
							class="world-map-region-outline"
							fill="none"
							stroke="#ffffff"
							stroke-width="0.5"
							pointer-events="none"
						/>
					</g>
				</template>
				<g v-else :key="`active-${location.id}`">
					<path
						:d="location.path"
						class="world-map-region"
						:class="regionClasses(location.id)"
						:style="activeRegionStyle(location)"
						@mouseenter="setCountryHover(location.id, $event)"
					/>
					<!-- Small countries: colored dot (same score color) — fill alone is too hard to see -->
					<circle
						v-if="needsEmphasisMarker(location)"
						class="world-map-emphasis"
						:cx="emphasisPoint(location).x"
						:cy="emphasisPoint(location).y"
						r="7"
						:fill="getRegionFill(location.id)"
						stroke="#ffffff"
						stroke-width="1.5"
						@mouseenter="setCountryHover(location.id, $event)"
					/>
				</g>
			</template>
		</svg>

		<div
			v-if="hoveredCountry"
			class="map-tooltip"
			:style="tooltipStyle"
			@mouseenter="cancelClearHover"
			@mouseleave="scheduleClearHover"
		>
			<p class="map-tooltip-title mb-1">
				{{ hoveredCountry.name }}
				<span v-if="hoveredCountry.suppliers.length > 1" class="map-tooltip-count">
					({{ hoveredCountry.suppliers.length }} suppliers)
				</span>
			</p>
			<p
				v-if="hoveredCountry.fillMode === 'split'"
				class="map-tooltip-note mb-2"
			>
				Split by supplier score: lowest on the left, highest on the right.
			</p>
			<p class="map-tooltip-average mb-2">
				Average score:
				<strong>{{ hoveredCountry.averageLabel }}</strong>
			</p>
			<ul class="map-tooltip-list mb-0 pl-0">
				<li
					v-for="supplier in hoveredCountry.suppliers"
					:key="`${supplier.name}-${supplier.clientID || supplier.score}`"
				>
					<router-link
						v-if="supplier.clientID"
						:to="supplierLink(supplier.clientID)"
						class="map-tooltip-link"
					>
						{{ supplier.name }}
					</router-link>
					<span v-else>{{ supplier.name }}</span>
					- {{ supplier.score }}
				</li>
			</ul>
		</div>
	</div>
</template>

<script>
import world from "@svg-maps/world"
import { MAP_TIER_COLORS } from "../utils/connectionMapChart"

const SMALL_PATH_SPAN = 1.2
// Only countries too tiny for a visible fill — everyone else is normal country color
const MICROSTATE_IDS = new Set([
	"ad", // Andorra
	"mc", // Monaco
	"sm", // San Marino
	"li", // Liechtenstein
	"va", // Vatican
	"mt", // Malta
	"sg", // Singapore
	"gi", // Gibraltar
	"lu", // Luxembourg
	"bh", // Bahrain
	"mv", // Maldives
	"sc", // Seychelles
	"nr", // Nauru
	"mo", // Macau
])

function pathAnchor(path) {
	if (!path || typeof path !== "string") {
		return null
	}
	const match = path.match(/[Mm]\s*([+-]?\d*\.?\d+)[,\s]+([+-]?\d*\.?\d+)/)
	if (!match) {
		return null
	}
	return {
		x: Number(match[1]),
		y: Number(match[2]),
	}
}

function pathSpan(path) {
	if (!path || typeof path !== "string") {
		return 0
	}
	const nums = path.match(/-?\d*\.?\d+/g)
	if (!nums || nums.length < 4) {
		return 0
	}
	const values = nums.map(Number).filter((n) => Number.isFinite(n))
	let x = 0
	let y = 0
	let minX = Infinity
	let maxX = -Infinity
	let minY = Infinity
	let maxY = -Infinity
	let i = 0
	// Approximate bbox from move/line absolute & relative command numbers
	while (i < values.length - 1) {
		const a = values[i]
		const b = values[i + 1]
		// Heuristic: treat pairs as coords; first pair absolute, later often relative
		if (i === 0) {
			x = a
			y = b
		} else if (Math.abs(a) > 20 || Math.abs(b) > 20) {
			x = a
			y = b
		} else {
			x += a
			y += b
		}
		minX = Math.min(minX, x)
		maxX = Math.max(maxX, x)
		minY = Math.min(minY, y)
		maxY = Math.max(maxY, y)
		i += 2
	}
	if (!Number.isFinite(minX)) {
		return 0
	}
	return Math.max(maxX - minX, maxY - minY)
}

export default {
	name: "SupplierPerformanceWorldMap",
	props: {
		countries: {
			type: Array,
			default: () => [],
		},
		enlargeSmallCountries: {
			type: Boolean,
			default: true,
		},
	},
	data() {
		return {
			world,
			MAP_TIER_COLORS,
			hoveredCountryId: null,
			tooltipX: 0,
			tooltipY: 0,
			hideTooltipTimer: null,
		}
	},
	computed: {
		countryById() {
			return this.countries.reduce((map, country) => {
				map[country.id] = country
				return map
			}, {})
		},
		inactiveLocations() {
			return this.world.locations.filter(
				(location) => !this.countryById[location.id]
			)
		},
		activeLocations() {
			return this.world.locations.filter((location) =>
				Boolean(this.countryById[location.id])
			)
		},
		splitCountries() {
			return this.countries.filter((country) => country.fillMode === "split")
		},
		splitClipPaths() {
			const clips = []

			for (const country of this.splitCountries) {
				const sliceCount = country.splitSlices?.length || 0
				for (let index = 0; index < sliceCount; index += 1) {
					clips.push({
						key: `${country.id}-${index}`,
						id: this.clipPathId(country.id, index),
						x: index / sliceCount,
						width: 1 / sliceCount,
					})
				}
			}

			return clips
		},
		hoveredCountry() {
			if (!this.hoveredCountryId) {
				return null
			}
			return this.countryById[this.hoveredCountryId] || null
		},
		tooltipStyle() {
			return {
				left: `${this.tooltipX}px`,
				top: `${this.tooltipY}px`,
			}
		},
	},
	beforeDestroy() {
		this.cancelClearHover()
	},
	methods: {
		clipPathId(countryId, index) {
			return `supplier-map-${countryId}-${index}`
		},
		isSplitCountry(locationId) {
			const country = this.countryById[locationId]
			return (
				country?.fillMode === "split" &&
				Array.isArray(country.splitSlices) &&
				country.splitSlices.length >= 2
			)
		},
		getSplitSlices(locationId) {
			return this.countryById[locationId]?.splitSlices || []
		},
		isSmallCountry(location) {
			return (
				MICROSTATE_IDS.has(location.id) ||
				pathSpan(location.path) < SMALL_PATH_SPAN
			)
		},
		needsEmphasisMarker(location) {
			return (
				this.enlargeSmallCountries &&
				Boolean(this.countryById[location.id]) &&
				this.isSmallCountry(location) &&
				Boolean(this.emphasisPoint(location))
			)
		},
		emphasisPoint(location) {
			return pathAnchor(location.path)
		},
		regionClasses(locationId) {
			return {
				"world-map-region--active": Boolean(this.countryById[locationId]),
				"world-map-region--hovered": this.hoveredCountryId === locationId,
			}
		},
		getRegionFill(locationId) {
			const country = this.countryById[locationId]
			if (!country) {
				return MAP_TIER_COLORS.default
			}
			return country.fill
		},
		activeRegionStyle(location) {
			return {
				fill: this.getRegionFill(location.id),
				stroke: "#ffffff",
				strokeWidth: 0.5,
			}
		},
		setCountryHover(locationId, event) {
			if (!this.countryById[locationId]) {
				return
			}
			this.cancelClearHover()
			this.hoveredCountryId = locationId
			if (event && this.$el) {
				const bounds = this.$el.getBoundingClientRect()
				this.tooltipX = event.clientX - bounds.left + 12
				this.tooltipY = event.clientY - bounds.top + 12
			}
		},
		scheduleClearHover() {
			this.cancelClearHover()
			this.hideTooltipTimer = window.setTimeout(() => {
				this.hoveredCountryId = null
				this.hideTooltipTimer = null
			}, 250)
		},
		cancelClearHover() {
			if (this.hideTooltipTimer) {
				window.clearTimeout(this.hideTooltipTimer)
				this.hideTooltipTimer = null
			}
		},
		supplierLink(clientID) {
			return {
				name: "Single Client View",
				params: { id: clientID },
				query: { focus: "performance" },
			}
		},
	},
}
</script>

<style scoped>
.supplier-performance-world-map {
	position: relative;
	width: 100%;
	min-height: 441px;
}

.world-map-svg {
	display: block;
	width: 100%;
	height: auto;
}

.world-map-region {
	stroke: #ffffff;
	stroke-width: 0.5;
	transition: fill 0.15s ease, opacity 0.15s ease;
}

.world-map-region--split {
	stroke-width: 0;
}

.world-map-region--active {
	cursor: pointer;
}

.world-map-emphasis {
	cursor: pointer;
}

.world-map-region--active:hover,
.world-map-region--hovered {
	opacity: 0.88;
	filter: brightness(0.92);
}

.map-tooltip {
	position: absolute;
	z-index: 2;
	max-width: 260px;
	padding: 12px 14px;
	border: 1px solid rgba(0, 0, 0, 0.12);
	border-radius: 8px;
	background: #ffffff;
	box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
	pointer-events: auto;
}

.map-tooltip-link {
	color: #1e7c83;
	font-weight: 600;
	text-decoration: none;
}

.map-tooltip-link:hover {
	text-decoration: underline;
}

.map-tooltip-title {
	font-size: 1rem;
	font-weight: 600;
	color: #1a1a1a;
}

.map-tooltip-count {
	font-size: 0.8125rem;
	font-weight: 500;
	color: #757575;
}

.map-tooltip-note {
	font-size: 0.8125rem;
	color: #757575;
	line-height: 1.4;
}

.map-tooltip-average {
	font-size: 0.875rem;
	color: #424242;
}

.map-tooltip-list {
	list-style: none;
	font-size: 0.8125rem;
	color: #616161;
	line-height: 1.5;
}
</style>
